import React, { useState, useEffect, useRef } from 'react';
import {
    List, Datagrid, TextField, FunctionField, SimpleList
} from 'react-admin';
import {
    useMediaQuery, makeStyles
} from '@material-ui/core';
import { customTheme } from "./customTheme";
import CustomSearch from './components/CustomSearch';
import { useLocation } from 'react-router-dom';
import CustomPagination from './components/CustomPagination';
import moment from 'moment';
import ScrollableTable from './components/ScrollableTable';
import UseAnalyticsEvent from './utils/useAnalyticsEvent';
import CustomFilter from './components/CustomFilter';
import CustomExportButton from './components/CustomExportButton';
import notify from './components/CustomUseNotify';
import config from "./config.json";
import queryString from 'query-string';
import { saveTokens } from './utils/saveTokens';
import CustomDateTimePicker from './components/CustomDateTimePickerV2';


const useStyles = makeStyles({
    list: {
        marginBottom: 0,
    },
    parkingSelectorContainer: {
        display: "grid",
        justifyContent: "space-between",
    },
});

const CATEGORY = "PURCHASED SECTION"

export const NewsletterSubscribers = props => {
    const eventTracker = UseAnalyticsEvent();
    const classes = useStyles();
    const location = useLocation();
    const listDataRef = useRef();

    const isSmallDevice = useMediaQuery(theme => customTheme.breakpoints.down("xs"));
    let prevSearch = sessionStorage.getItem(location.pathname) ? JSON.parse(sessionStorage.getItem(location.pathname)) : ""
    const [search, setSearch] = useState(prevSearch.inputSearch)
    let searchTypeState = ["Email"]
    const [searchTypes, setSearchTypes] = useState(prevSearch.type ? prevSearch.type : "Email");
    const [settingFilterFormat, setSettingFilterFormat] = useState({})

    const [startDateTime, setStartDateTime] = useState(null);
    const [endDateTime, setEndDateTime] = useState(null);
    const [startDateTimeFilter, setStartDateTimeFilter] = useState(null);
    const [endDateTimeFilter, setEndDateTimeFilter] = useState(null);
    const [use24HourFormat] = useState(sessionStorage.getItem("use_24_hour_format") === "1");
	const [showTimeTillSeconds] = useState(sessionStorage.getItem("show_time_till_seconds") === "1");

	const renderTime = time => {
		const secondsFormat = showTimeTillSeconds ? ":ss" : "";
        if(use24HourFormat) return moment(time, "YYYY-MM-DD HH:mm:ss").format(`DD/MM/YYYY HH:mm${secondsFormat}`)
        return moment(time, "YYYY-MM-DD HH:mm:ss").format(`DD/MM/YYYY hh:mm${secondsFormat} A`)
	};

    const filter = { ...settingFilterFormat, start_time: startDateTimeFilter ? moment(startDateTimeFilter).format("YYYY-MM-DD HH:mm:ss") : startDateTime ? moment(startDateTime).format("YYYY-MM-DD HH:mm:ss")  :  "", end_time: endDateTimeFilter ? moment(endDateTimeFilter).format("YYYY-MM-DD HH:mm:ss") : endDateTime ? moment(endDateTime).format("YYYY-MM-DD HH:mm:ss") : "", search: { value: search ? search : "", type: searchTypes ? searchTypes : "" } };


    const [filterListValue, setFilterListValue] = useState(prevSearch.radioValue ? prevSearch.radioValue : null)
    let filterTypesInitState = [
        {
            filterType: "Subscribers Status",
            dataList: [{ name: "Subscribed", id: 1 }, { name: "UnSubscribed", id: 2 }],
            listType: "Static",
            caseType: "Static",
            showFilter: true
        },
    ]


    const getExportFile = (fileType) => {
        const {current : {total, currentSort, filterValues}} = listDataRef
        const params = {
            filter: JSON.stringify({ ...filter, ...filterValues }),
            sort: JSON.stringify([currentSort.field, currentSort.order]),
            page: 1,
            perPage: total,
            user_id: sessionStorage.getItem("user_id")
        };
        fetch(`${config.HOST_NAME}/crm/v3/newsletterSubscribers?${queryString.stringify(params)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'RefreshToken': `Bearer ${sessionStorage.getItem('refresh_token')}`,
                'Accept': `application/${fileType}`,
            },
        })
            .then(response => {
                saveTokens(response)
                return response.blob()
            })
            .then(blob => {
                const url = URL.createObjectURL(blob);
                const tempLinkElement = document.createElement('a');
                tempLinkElement.href = url;
                tempLinkElement.setAttribute('download', `Newsletter Subscribers Data.${fileType}`);
                tempLinkElement.click();
            })
            .catch(error => {
                notify('Error Exporting Data', 'warning');
            });
    };

    const getExcelFile = () => {
        getExportFile("xlsx");
    };

    const setTimeFilters = () => {
        setStartDateTimeFilter(startDateTime);
        setEndDateTimeFilter(endDateTime);
    };

    useEffect(() => {
        eventTracker(CATEGORY, "NEWSLETTER SUBSCRIBERS LIST")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        let values = {}
        // Add more types based on the filters
        if (filterListValue) {
            for (let val in filterListValue) {
                if (filterListValue[val].filterType === "Subscribers Status") {
                    values = { ...values, subscribe_status: filterListValue[val].value ? filterListValue[val].value : undefined }
                }
            }
            setSettingFilterFormat(values)
        }
        if (filterListValue === null) {
            setSettingFilterFormat({})
        }
    }, [filterListValue]) // eslint-disable-line react-hooks/exhaustive-deps

    return <div className={classes.list}>
        <div className='customStyleForDataSearchDiv'>
            <CustomSearch search={search} setSearch={setSearch} searchTypes={searchTypes} setSearchTypes={setSearchTypes} searchTypeState={searchTypeState} pathName={location.pathname} />
            <CustomDateTimePicker startDateTime={startDateTime} setStartDateTime={setStartDateTime} endDateTime={endDateTime} setEndDateTime={setEndDateTime} onChange={setTimeFilters} />
            <CustomFilter filterTypesInitState={filterTypesInitState} filterListValue={filterListValue} setFilterListValue={setFilterListValue} pathName={location.pathname} />
            <CustomExportButton getCSV={getExcelFile} disabled={listDataRef.current && listDataRef.current.total === 0} />
        </div>

        <List
            {...props}
            style={{marginTop: 20}}
            title="Newsletter Subscribers"
            bulkActionButtons={false}
            exporter={false}
            filter={filter}
            empty={false}
            pagination={<CustomPagination setListData={(listData) => {listDataRef.current = listData}} />}
            actions={false}
        >
            {
                isSmallDevice ?
                    <SimpleList
                        primaryText={record => record.email}
                        secondaryText={record => (<div>
                            {record.email && <div><span style={{ color: "#5a5a5a" }}>Email: </span>{record.email}</div>}
                            {record.unsubscribe && <div><span style={{ color: "#5a5a5a" }}>Subscriber: </span>{record.unsubscribe === 0 ? 'Yes' : 'No'}</div>}
                            {record.reason && <div><span style={{ color: "#5a5a5a" }}>Reason: </span>{record.reason}</div>}

                            {record.subscribe_time && <div><span style={{ color: "#5a5a5a" }}>Subscribe Time: </span>{renderTime(record["subscribe_time"])}</div>}
                            {record.unsubscribe_time && <div><span style={{ color: "#5a5a5a" }}>Subscribe Time: </span>{renderTime(record["unsubscribe_time"])}</div>}

                        </div>)}
                        onContextMenu={event => event.preventDefault()}
                        linkType={false}
                        rowStyle={() => ({ alignItems: "flex-start", borderBottom: "1px solid #18cc69" })}
                    />
                    :
                    <Datagrid component={ScrollableTable}>
                        <TextField source='email' label="Email" />
                        <FunctionField source='unsubscribe' label="Subscribed" render={record => record && <span style={{ fontWeight: "bold", color: record.unsubscribe === 0 ? '#18cc69' : '#de575e' }}>{record.unsubscribe === 0 ? "Yes" : "No"}</span>} />
                        <FunctionField source="subscribe_time" label="Subscribed On" render={record => record["subscribe_time"] ? renderTime(record["subscribe_time"]) : '-'} />
                        <FunctionField source="unsubscribe_time" label="Unsubscribed On" render={record => record["unsubscribe_time"] ? renderTime(record["unsubscribe_time"]) : '-'} />
                        <FunctionField source='reason' label="Reason" render={record => record && <span>{record.reason ? record.reason : '-'}</span>} />

                    </Datagrid>
            }
        </List>
    </div>
};